(function () {
  const { Component } = window;

  const style = `
    :host {
      display: flex;
      overflow-x: auto;
      position: relative;

      --paddingLeft: 0;
      --paddingRight: 0;
      --tabBarActiveBarColor: var(--blue-800, #4691e3);
      --tabBarActiveTextColor: var(--neutral-100, #e7e7e7);
      --tabBarTextColor: var(--neutral-300, #949494);
    }

    :host([skeleton]) #container  { display: none; }
    :host([skeleton]) #underscore { display: none; }
    :host([hidden]), .hide { display: none !important; }

    #container {
      width: 100%;
      display: flex;
      justify-content: var(--align, flex-start);
      padding-left: var(--paddingLeft);
      padding-right: var(--paddingRight);
    }

    #container.fit-content {
      width: auto;
    }

    #underscore {
      background-color: var(--tabBarActiveBarColor);
      border-radius: 3px;
      bottom: 0;
      height: 3px;
      position: absolute;
      transform: translateX(calc(var(--underscoreX, 100px) + 12px)) scaleX(var(--underscoreWidth, 1));
      transition: transform 0.2s ease-in-out;
      width: 1px;
    }

    #underscore.remove-transition {
      transition: none;
    }
  `;

  const template = document.createElement('template');
  template.innerHTML = `
    <style>${style}</style>
    <div id="container">
      <slot name="option" @click="_onOptionClick" @keydown="_onOptionKeyDown"></slot>
    </div>
    <div id="underscore" class="remove-transition"></div>
  `;

  class TabBar extends Component {
    static get observedAttributes() {
      return ['align', 'hidden', 'name', 'role', 'value'];
    }

    static get propsCamelConventionEnabled() {
      return true;
    }

    constructor() {
      super();

      this.initBool(['hidden']);
      this.initSlotted();
    }

    async connectedCallback() {
      await this.elementsDefined('tab-bar-option');

      this._setDefaultAttributes();
      this._setDefaultSlotsAttributes();

      this._setResizeListener();
      this._adjustContent();

      await this._init();
      this.ready();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this._resizeObs.disconnect();
    }

    revealActive() {
      if (!this.value) {
        return;
      }

      const elActive = this._getOptionByVal(this.value);
      if (this._isOptionFullyContained(elActive)) {
        return;
      }
      this.scrollTo({ left: elActive.offsetLeft, behavior: 'smooth' });
    }

    template() {
      return template;
    }

    _adjustContent() {
      const elContainer = this.select('#container');
      const isOverflow = elContainer.scrollWidth > this.offsetWidth;

      if (isOverflow) {
        elContainer.classList.add('fit-content');
        return;
      }

      elContainer.classList.remove('fit-content');

      if (!this._isMobile) {
        return;
      }
      this.align = 'stretch';
    }

    _attributeAlign(newVal) {
      const flexMapping = { stretch: 'flex-start', left: 'flex-start', center: 'center', right: 'flex-end' };
      this.style.setProperty('--align', flexMapping[newVal] || flexMapping.left);
      const action = newVal === 'stretch' ? 'add' : 'remove';
      this.slotted.option.forEach(elOpt => elOpt.classList[action]('stretch'));

      this._updateUnderscore(this._getOptionByVal(this.value));
    }

    _attributeHidden() {
      if (this.hidden) {
        return;
      }
      this._updateUnderscore(this._getOptionByVal(this.value));
    }

    _attributeValue(newVal, oldVal) {
      const elPrevOption = this._getOptionByVal(oldVal);
      if (elPrevOption) {
        elPrevOption.setAttribute('aria-selected', false);
        elPrevOption.active = false;
      }

      const elNewOption = this._getOptionByVal(newVal);
      this._updateUnderscore(elNewOption);
      if (!elNewOption) {
        return;
      }

      elNewOption.active = true;
      elNewOption.setAttribute('aria-selected', true);
      this.revealActive();

      if (oldVal !== undefined) {
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    _getOptionByVal(val) {
      const getOptionWC = el => el.tagName === 'TAB-BAR-OPTION' ? el : el.querySelector('tab-bar-option');
      const elOptionSlot = this.slotted.option.find(elOpt => getOptionWC(elOpt).value === val);
      if (!elOptionSlot) {
        return;
      }
      return getOptionWC(elOptionSlot);
    }

    async _init() {
      if (this.value) {
        await document.fonts?.ready;
      }

      this._updateUnderscore(this._getOptionByVal(this.value));
      this.revealActive();

      this.select('#underscore').classList.remove('remove-transition');
    }

    _isOptionFullyContained(elOption) {
      const containerLowerLimit = this.scrollLeft;
      const containerUpperLimit = containerLowerLimit + this.offsetWidth;

      const elementLowerLimit = elOption.offsetLeft;
      const elementUpperLimit = elementLowerLimit + elOption.offsetWidth;

      return containerLowerLimit <= elementLowerLimit && elementUpperLimit <= containerUpperLimit;
    }

    _onOptionClick(e) {
      const option = e.target.closest('tab-bar-option');
      if (option) {
        this.value = option.value;
      }
    }

    _onOptionKeyDown(e) {
      if (e.key !== 'Enter') {
        return;
      }
      const option = e.target.closest('tab-bar-option');
      if (!option) {
        return;
      }

      // Anchored options navigate natively on Enter (the anchor is the focus
      // target); only drive value for plain-text tabs.
      if (option.querySelector('a[href]')) {
        return;
      }

      this.value = option.value;
    }

    _setDefaultAttributes() {
      if (!this.role) {
        this.role = 'tablist';
      }
      if (!this.align) {
        this.align = 'left';
      }
      if (this.value) {
        this._attributeValue(this.value);
      }
    }

    _setDefaultSlotsAttributes() {
      const defaults = [
        ['role', 'tab'],
        ['aria-selected', false]
      ];
      this.slotted.option.forEach((elOpt) => {
        defaults.forEach(([attr, value]) => {
          if (elOpt.hasAttribute(attr)) {
            return;
          }
          elOpt.setAttribute(attr, value);
        });

        // Keep a single focus stop per tab. When the option contains an
        // anchor, the anchor is the natural tab stop, so the host must stay
        // out of the tab order. Only plain-text options get a host tabindex.
        const hasAnchor = !!elOpt.querySelector('a[href]');
        if (!hasAnchor && !elOpt.hasAttribute('tabindex')) {
          elOpt.setAttribute('tabindex', 0);
        }
      });
    }

    _setResizeListener() {
      const onResize = () => {
        this._adjustContent();
        this._updateUnderscore(this._getOptionByVal(this.value));
      };
      this._resizeObs = new ResizeObserver(onResize);
      this._resizeObs.observe(this);
    }

    _updateUnderscore(el) {
      const elUnderscore = this.select('#underscore');
      if (!el) {
        return elUnderscore.hidden = true;
      }

      elUnderscore.hidden = false;
      const underscoreWidth = el.offsetWidth - 24;
      const underscoreX = el.offsetLeft + (underscoreWidth / 2);
      this.style.setProperty('--underscoreWidth', underscoreWidth);
      this.style.setProperty('--underscoreX', `${underscoreX}px`);
    }
  }

  if (!window.customElements.get('tab-bar')) {
    window.customElements.define('tab-bar', TabBar);
  }

  /* istanbul ignore next */
  if (typeof module !== 'undefined') {
    module.exports = TabBar;
  }
})();
